<template>
  <div class="sales-contact-form">
    <Loading v-if="waiting" />
    <!--
      @todo should we add keyboard support (arrow keys & enter)?
      you also can't move through the 'form' with tab
      you can make this form, but the <form> has lots of
      styles applied to it, so you'd need to refactor that also
      (so, do it later, I guess)
     -->
    <template v-if="countries.length">
      <!-- Country select (always present). -->
      <div
        class="sales-contact-form__select sales-contact-form__select--country">
        <SingleSelect
          :options="countries"
          :label="t.countryPlaceholder"
          :defaultCountryId="selectedCountryId"
          v-model="selectedCountryId"
          :searchable="true"
          :searchPlaceholder="t.searchPlaceholder"
          @update:modelValue="checkForExtraFields" />
      </div>

      <!-- Extra fields for certain countries only. -->
      <template v-if="!showOnlyCountry">
        <!-- Region select. -->
        <div
          class="sales-contact-form__select sales-contact-form__select--regions"
          v-if="showingRegionsSelect">
          <SingleSelect
            :options="availableCountryRegions"
            :label="t.regionsPlaceholder"
            v-model="selectedRegionId" />
        </div>
        <!-- Crop select. -->
        <div
          class="sales-contact-form__select sales-contact-form__select--crops"
          v-if="showingCropsSelect">
          <SingleSelect
            :options="availableCountryCrops"
            :label="t.cropsPlaceholder"
            v-model="selectedCropId" />
        </div>
        <!-- ZIP code input. -->
        <div class="sales-contact-form__select" v-if="showingZipCodeSelect">
          <input
            type="number"
            v-model="selectedZipCode"
            :placeholder="t.zipCodePlaceholder" />
        </div>
      </template>

      <!-- Submit button. -->
      <button
        class="button sales-contact-form__action-button"
        :disabled="isDisabled"
        @click="findContact">
        {{ t.buttonText }}
      </button>
    </template>
  </div>
</template>

<script>
// components
import Loading from '../elements/Loading';
import SingleSelect from '../elements/SingleSelect';
// utils
import translationStrings from '../../translations/translationStrings';
import { getSalesContacts } from '../../utils/api';
import { getState, setState, removeState } from '../../utils/persistedState';

export default {
  components: {
    Loading,
    SingleSelect,
  },
  props: {
    lang: {
      type: String,
      required: true,
    },
    defaultCountryId: {
      type: Number,
      required: false,
      default: 0,
    },
    currentCountryId: {
      type: Number,
      required: false,
      default: 0,
    },
    showOnlyCountry: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      countries: [],
      salesContacts: [],
      salesContactsData: {},
      selectedCountryId: 0,
      selectedCropId: 0,
      selectedRegionId: 0,
      selectedZipCode: '',
      t: translationStrings,
      waiting: true,
    };
  },
  computed: {
    isDisabled() {
      if (!this.selectedCountryId) {
        return true;
      }

      const hasContacts = this.availableCountryContacts.length > 0;
      const regionCondition =
        this.availableCountryRegions.length > 0
          ? this.selectedRegionId !== 0
          : true;
      const cropCondition =
        this.availableCountryCrops.length > 0
          ? this.selectedCropId !== 0
          : true;
      const zipCondition = this.zipCodesExist && this.selectedZipCode !== '';

      if (hasContacts && regionCondition && cropCondition) {
        return false;
      }

      if (
        (this.availableCountryRegions.length && !this.selectedRegionId) ||
        (this.availableCountryCrops.length && !this.selectedCropId) ||
        zipCondition
      ) {
        return !this.showOnlyCountry;
      }

      return false;
    },
    availableCountryContacts() {
      return (
        this.salesContacts.filter((contact) =>
          contact.countries.some((c) => c.id === this.selectedCountryId),
        ) ?? []
      );
    },
    availableCountryRegions() {
      const countryRegions = this.availableCountryContacts
        .flatMap(({ countries }) => countries)
        .filter(({ id }) => id === this.selectedCountryId)
        .flatMap(({ regions }) => regions);

      // Get unique values and sort them alphabetically.
      return [...new Map(countryRegions.map((r) => [r.id, r])).values()].sort(
        (a, b) => a.name.localeCompare(b.name),
      );
    },
    availableCountryCrops() {
      const allContactCrops =
        this.availableCountryContacts.flatMap(({ crops }) => crops) ?? [];
      // Return unique values.
      const lookup = new Set();

      return allContactCrops.filter(
        (item) => !lookup.has(item.id) && lookup.add(item.id),
      );
    },
    hasContactCountry() {
      // Default check for displaying fields.
      return !!this.availableCountryContacts.length && !!this.selectedCountryId;
    },
    showingRegionsSelect() {
      return this.hasContactCountry && this.availableCountryRegions.length > 0;
    },
    showingCropsSelect() {
      if (this.hasContactCountry && this.availableCountryCrops.length > 0) {
        // If it has regions ...
        if (this.availableCountryRegions.length > 0) {
          // ... and one is selected
          return this.selectedRegionId !== 0;
        }

        // Otherwise show it (bc first condition is met already).
        return true;
      }

      return false;
    },
    showingZipCodeSelect() {
      if (this.hasContactCountry && this.zipCodesExist) {
        const regions = this.availableCountryRegions.length > 0;
        const crops = this.availableCountryCrops.length > 0;

        // If it has regions & crops ...
        if (regions && crops) {
          // ... make sure one is selected
          return this.selectedRegionId !== 0 && this.selectedCropId !== 0;
        }
        // If it has regions only ...
        if (regions && !crops) {
          // ... make sure region is selected
          return this.selectedRegionId !== 0;
        }
        // If it has crops only ...
        if (!regions && crops) {
          // ... make sure crop is selected
          return this.selectedCropId !== 0;
        }
      }

      // Otherwise don't display it.
      return false;
    },
    hasAdditionalFields() {
      return (
        this.availableCountryRegions.length > 0 ||
        this.availableCountryCrops.length > 0 ||
        this.zipCodesExist
      );
    },
    zipCodesExist() {
      return (
        [
          ...new Set(
            this.availableCountryContacts.flatMap(({ codes }) => codes),
          ),
        ].length > 0
      );
    },
  },
  watch: {
    currentCountryId(newId) {
      this.selectedCountryId = newId;
    },
    // Reset any selected data on input change.
    selectedCountryId() {
      this.selectedCropId = 0;
      this.selectedRegionId = 0;
      this.selectedZipCode = '';
    },
  },
  async mounted() {
    // Fetch all data on mounted.
    try {
      const { data } = await getSalesContacts(this.lang);
      this.salesContactsData = data;

      if (!this.objectEmpty(this.salesContactsData)) {
        this.salesContacts = this.salesContactsData?.contacts;
        this.countries = this.salesContactsData?.countries;

        // Pre-select user's country (based on language).
        this.selectedCountryId = Number(
          this.salesContactsData?.default_country,
        );
      }

      // If the parent propped a default country, select it.
      if (this.defaultCountryId) {
        this.selectedCountryId = this.defaultCountryId;
      }

      // If country was pre-selected for any reason, check for region/crop/ZIP code.
      this.$nextTick(() => {
        if (this.selectedCountryId && this.hasAdditionalFields) {
          const savedState = getState();
          if (savedState) {
            this.selectedRegionId = this.availableCountryRegions.find(
              (r) => r.id === savedState?.region,
            )?.id;
            this.selectedCropId = this.availableCountryCrops.find(
              (c) => c.id === savedState?.crop,
            )?.id;
            this.selectedZipCode = savedState?.zipCode;
          }
        }
      });
    } catch ({ response }) {
      if (response) {
        console.error(response);
      }
    } finally {
      // Disable loading.
      this.waiting = false;
      this.$emit('onLoadingStop', this.waiting);
    }
  },
  methods: {
    findContact() {
      // When in country-only mode, open the modal if region/crop/ZIP is missing.
      if (this.showOnlyCountry && this.hasAdditionalFields) {
        this.$emit('modalRequested');
        return;
      }

      // abort if country isn't selected
      if (this.selectedCountryId == null || this.selectedCountryId === 0) {
        return;
      }

      let result = {};

      // first check if zip coeds exist and if one is selected
      // => meaning all other fields are checked already
      // => BUT, there can also be a case when either regions or crops are not needed
      // and it's COUNTRY + ZIP only
      if (this.zipCodesExist) {
        result = this.availableCountryContacts.find(
          ({ countries, crops, codes }) => {
            return (
              ((this.availableCountryRegions.length > 0
                ? this.hasSelectedRegion(countries)
                : true) &&
                (this.availableCountryCrops.length > 0
                  ? this.hasSelectedCrop(crops)
                  : true) &&
                (this.selectedZipCode === ''
                  ? codes.length === 0
                  : this.hasSelectedZipCode(codes))) ??
              {}
            );
          },
        );
      }
      // then check if crops and regions exist and get the results for COUNTRY + REGIONS + CROP
      else if (
        this.availableCountryRegions.length > 0 &&
        this.availableCountryCrops.length > 0
      ) {
        result =
          this.availableCountryContacts.find(({ countries, crops }) => {
            return (
              this.hasSelectedRegion(countries) && this.hasSelectedCrop(crops)
            );
          }) ?? {};
      }
      // then check if crop exist and get the result for COUNTRY + CROP
      else if (this.availableCountryCrops.length > 0) {
        result =
          this.availableCountryContacts.find(({ crops }) => {
            return this.hasSelectedCrop(crops);
          }) ?? {};
      }
      // then check if regions exist and get the result for COUNTRY + REGION
      else if (this.availableCountryRegions.length > 0) {
        result =
          this.availableCountryContacts.find(({ countries }) => {
            return this.hasSelectedRegion(countries);
          }) ?? {};
      }
      // lastly, check if country exists and get the results for this
      else {
        result =
          this.availableCountryContacts.find(({ countries }) =>
            countries.some((c) => c.id === this.selectedCountryId),
          ) ?? {};
      }

      // Save the result in local storage (if it's not null).
      // We save only IDs as the current lang could change at any point.
      if (result?.id) {
        setState('salesContact', {
          id: result.id || 0,
          country: this.selectedCountryId,
          crop: this.selectedCropId,
          region: this.selectedRegionId,
          zipCode:
            Number(this.selectedZipCode) === 0 ? '' : this.selectedZipCode,
        });
      } else if (this.objectEmpty(result)) {
        // if it's empty, remove the data from the local storage
        removeState();
      }

      // Let the parent know that the selection was updated.
      this.$emit('updated', {
        result,
        countryId: this.selectedCountryId,
      });
    },
    hasSelectedRegion(countries) {
      return countries
        .flatMap((c) => c.regions)
        .some((r) => r.id === this.selectedRegionId);
    },
    hasSelectedCrop(crops) {
      return crops.some((c) => c.id === this.selectedCropId);
    },
    hasSelectedZipCode(codes) {
      // Check if selected zip code is a number.
      if (Number(this.selectedZipCode) !== 0) {
        return codes.includes(Number(this.selectedZipCode));
      }

      return false;
    },
    checkForExtraFields() {
      if (this.hasAdditionalFields) {
        // Let the parent know that it has to open a modal for additional fields.
        this.$emit('modalRequested', this.selectedCountryId);
      }
    },
    objectEmpty(obj) {
      return obj == null ? true : Object.keys(obj).length === 0;
    },
  },
};
</script>
